/** Images, figures and captions. */

import { colour, space } from '../tokens.js';
import { BOX_RESET, font, TEXT_RESET, type StyleDef } from '../styleDef.js';

/**
 * Images always carry explicit width/height attributes (enforced in the emitter), so this
 * class only handles fluid scaling. `height: auto` plus the attributes is what lets the
 * browser reserve the right box before the bytes arrive, which is the CLS half of the
 * Core Web Vitals story.
 */
export const lpImage: StyleDef = {
  name: 'lp-image',
  base: {
    ...BOX_RESET,
    display: 'block',
    width: '100%',
    height: 'auto',
    'max-width': '100%',
    'min-width': '0',
    'object-fit': 'cover',
    'border-top-left-radius': '12px',
    'border-top-right-radius': '12px',
    'border-bottom-left-radius': '12px',
    'border-bottom-right-radius': '12px',
  },
};

/** Square corners, for logos and app-store badges where a radius would look wrong. */
export const lpImagePlain: StyleDef = {
  name: 'is-plain',
  comb: true,
  parent: 'lp-image',
  base: {
    'border-top-left-radius': '0px',
    'border-top-right-radius': '0px',
    'border-bottom-left-radius': '0px',
    'border-bottom-right-radius': '0px',
    'object-fit': 'contain',
  },
};

export const lpFigure: StyleDef = {
  name: 'lp-figure',
  base: {
    ...BOX_RESET,
    display: 'block',
    width: '100%',
    'margin-top': '0px',
    'margin-bottom': '0px',
    'margin-left': '0px',
    'margin-right': '0px',
  },
};

export const lpCaption: StyleDef = {
  name: 'lp-caption',
  base: {
    ...TEXT_RESET,
    ...font('small'),
    color: colour.inkMuted,
    'margin-top': space[2],
    'text-align': 'left',
  },
};

/** Small circular icon chip. One tinted chip per card is the whole colour budget. */
export const lpIconChip: StyleDef = {
  name: 'lp-icon-chip',
  base: {
    ...BOX_RESET,
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    width: '44px',
    height: '44px',
    'background-color': colour.surfaceAlt,
    'border-top-left-radius': '10px',
    'border-top-right-radius': '10px',
    'border-bottom-left-radius': '10px',
    'border-bottom-right-radius': '10px',
  },
};

/**
 * Image width and radius.
 *
 * These have to be combos of `lp-image` rather than reusing the `lp-box` width utilities,
 * because a Webflow combo attaches to exactly one base class and an <img> is not a box.
 */
const imgCombo = (name: string, base: StyleDef['base']): StyleDef => ({
  name,
  comb: true,
  parent: 'lp-image',
  base,
});

export const imageWidthCombos: StyleDef[] = [
  imgCombo('is-img-full', { width: '100%', 'max-width': '100%' }),
  imgCombo('is-img-two-thirds', { width: '66.666%', 'max-width': '66.666%' }),
  imgCombo('is-img-half', { width: '50%', 'max-width': '50%' }),
  imgCombo('is-img-third', { width: '33.333%', 'max-width': '33.333%' }),
];

export const imageRadiusCombos: StyleDef[] = [
  imgCombo('is-img-radius-none', {
    'border-top-left-radius': '0px',
    'border-top-right-radius': '0px',
    'border-bottom-left-radius': '0px',
    'border-bottom-right-radius': '0px',
  }),
  imgCombo('is-img-radius-sm', {
    'border-top-left-radius': '6px',
    'border-top-right-radius': '6px',
    'border-bottom-left-radius': '6px',
    'border-bottom-right-radius': '6px',
  }),
  imgCombo('is-img-radius-lg', {
    'border-top-left-radius': '20px',
    'border-top-right-radius': '20px',
    'border-bottom-left-radius': '20px',
    'border-bottom-right-radius': '20px',
  }),
];

/**
 * How the image fills its box.
 *
 * `cover` crops to fill, `contain` letterboxes, `fill` stretches. Paired with a fixed aspect
 * ratio below, `cover` is what makes a row of differently-shaped photos line up.
 */
export const imageFitCombos: StyleDef[] = [
  imgCombo('is-img-fit-cover', { 'object-fit': 'cover' }),
  imgCombo('is-img-fit-contain', { 'object-fit': 'contain', 'background-color': colour.surface }),
  imgCombo('is-img-fit-fill', { 'object-fit': 'fill' }),
];

/**
 * A fixed aspect ratio.
 *
 * This is the other half of making images uniform: without it a portrait photo next to a
 * landscape one produces a ragged row however you set the widths. With a ratio plus
 * `object-fit: cover`, every image in a grid occupies an identical box.
 */
export const imageRatioCombos: StyleDef[] = (
  [
    ['16-9', '16 / 9'],
    ['3-2', '3 / 2'],
    ['4-3', '4 / 3'],
    ['1-1', '1 / 1'],
    ['3-4', '3 / 4'],
  ] as const
).map(([name, value]) =>
  imgCombo(`is-img-ratio-${name}`, { 'aspect-ratio': value, height: '100%', 'object-fit': 'cover' }),
);

/** A 1px rule. Its surrounding space comes from the container, not from a margin. */
export const lpDivider: StyleDef = {
  name: 'lp-divider',
  base: {
    ...BOX_RESET,
    display: 'block',
    width: '100%',
    height: '1px',
    'background-color': colour.border,
  },
};

export const mediaStyles: StyleDef[] = [
  lpImage,
  lpImagePlain,
  lpFigure,
  lpCaption,
  lpIconChip,
  lpDivider,
  ...imageWidthCombos,
  ...imageRadiusCombos,
  ...imageFitCombos,
  ...imageRatioCombos,
];
